import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FlaskConical, Plus, Power, RotateCcw, ShieldCheck, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { timeSavedApi } from '@/api/timeSaved';
import { orgsApi } from '@/api/orgs';
import { Button, EmptyState, Loading, Modal } from '@/components/ui';

function formatDate(value: string | null): string {
  if (!value) return '—';
  return new Intl.DateTimeFormat('he-IL', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value));
}

export default function BetaProgramsSection({
  programKey,
  title,
  description,
}: {
  programKey: string;
  title: string;
  description: string;
}) {
  const queryClient = useQueryClient();
  const programQueryKey = ['beta-program', programKey] as const;
  const [modalOpen, setModalOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [organizationId, setOrganizationId] = useState('');
  const [notes, setNotes] = useState('');

  const programQuery = useQuery({
    queryKey: programQueryKey,
    queryFn: () => timeSavedApi.betaProgram(programKey),
  });

  const candidatesQuery = useQuery({
    queryKey: ['beta-program-org-candidates', search],
    queryFn: () => orgsApi.list({ search: search.trim() || undefined, perPage: 50 }),
    enabled: modalOpen,
  });

  const activeOrganizationIds = useMemo(
    () => new Set(programQuery.data?.organizations.filter((row) => row.status === 'active').map((row) => row.organization_id) ?? []),
    [programQuery.data],
  );

  const candidates = useMemo(
    () => (candidatesQuery.data?.items ?? []).filter(
      (org) => org.data_classification === 'real' && !activeOrganizationIds.has(org.id),
    ),
    [activeOrganizationIds, candidatesQuery.data],
  );

  const refresh = async () => queryClient.invalidateQueries({ queryKey: programQueryKey });

  const toggleMutation = useMutation({
    mutationFn: (enabled: boolean) => timeSavedApi.setBetaEnabled(enabled, programKey),
    onSuccess: async () => { await refresh(); toast.success('Beta kill switch updated'); },
    onError: (error: Error) => toast.error(error.message),
  });

  const enrollMutation = useMutation({
    mutationFn: ({ orgId, enrollmentNotes }: { orgId: string; enrollmentNotes: string | null }) =>
      timeSavedApi.enrollBetaOrganization(orgId, enrollmentNotes, programKey),
    onSuccess: async () => {
      await refresh();
      setModalOpen(false);
      setOrganizationId('');
      setNotes('');
      toast.success(`Organization added to ${title}`);
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const revokeMutation = useMutation({
    mutationFn: (organizationId: string) => timeSavedApi.revokeBetaOrganization(organizationId, programKey),
    onSuccess: async () => { await refresh(); toast.success('Beta access revoked'); },
    onError: (error: Error) => toast.error(error.message),
  });

  if (programQuery.isLoading) return <Loading text="Loading beta program…" />;
  if (programQuery.error || !programQuery.data) {
    return <EmptyState title="Beta program unavailable" description={(programQuery.error as Error)?.message ?? 'No program data returned.'} />;
  }

  const program = programQuery.data;
  const activeRows = program.organizations.filter((row) => row.status === 'active');
  const revokedRows = program.organizations.filter((row) => row.status === 'revoked');

  return (
    <section className="card p-6 space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="rounded-lg bg-gold/15 p-2 text-gold"><FlaskConical className="h-5 w-5" /></div>
          <div>
            <h2 className="text-lg font-semibold text-ink-primary">{title}</h2>
            <p className="mt-1 text-sm text-ink-secondary">{description}</p>
          </div>
        </div>
        <label className="flex items-center gap-3 rounded-lg border border-navy-border bg-navy px-3 py-2 text-sm text-ink-primary">
          <Power className={program.enabled ? 'h-4 w-4 text-success' : 'h-4 w-4 text-danger'} />
          <span>{program.enabled ? 'Program active' : 'Program paused'}</span>
          <input
            type="checkbox"
            checked={program.enabled}
            disabled={toggleMutation.isPending}
            onChange={(event) => toggleMutation.mutate(event.target.checked)}
            className="h-4 w-4 accent-gold"
            aria-label={`${title} global beta kill switch`}
          />
        </label>
      </div>

      {!program.enabled && (
        <div className="rounded-lg border border-danger/40 bg-danger/10 px-4 py-3 text-sm text-danger">
          Kill switch is off. No enrolled organization currently has access.
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm text-ink-secondary"><strong className="text-ink-primary">{activeRows.length}</strong> active beta organizations</div>
        <Button type="button" size="sm" onClick={() => setModalOpen(true)}><Plus className="h-4 w-4" />Add organization</Button>
      </div>

      {activeRows.length === 0 ? (
        <EmptyState title="No active beta testers" description="Add a real organization to begin the private pilot." />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-navy-border text-left text-ink-secondary"><th className="px-3 py-2 font-medium">Organization</th><th className="px-3 py-2 font-medium">Classification</th><th className="px-3 py-2 font-medium">Added</th><th className="px-3 py-2 font-medium">Added by</th><th className="px-3 py-2 font-medium">Notes</th><th className="px-3 py-2" /></tr></thead>
            <tbody>
              {activeRows.map((row) => (
                <tr key={row.organization_id} className="border-b border-navy-border/60">
                  <td className="px-3 py-3"><div className="font-medium text-ink-primary">{row.organization_name}</div><div className="text-xs text-ink-muted">{row.organization_slug} · {row.organization_id}</div></td>
                  <td className="px-3 py-3 text-ink-secondary">{row.data_classification}</td>
                  <td className="px-3 py-3 text-ink-secondary whitespace-nowrap">{formatDate(row.enrolled_at)}</td>
                  <td className="px-3 py-3 text-ink-secondary">{row.enrolled_by_email ?? 'Migration seed'}</td>
                  <td className="px-3 py-3 text-ink-secondary">{row.notes ?? '—'}</td>
                  <td className="px-3 py-3 text-right"><Button type="button" variant="danger" size="sm" loading={revokeMutation.isPending && revokeMutation.variables === row.organization_id} onClick={() => revokeMutation.mutate(row.organization_id)}><Trash2 className="h-4 w-4" />Revoke</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex items-start gap-2 rounded-lg bg-success/10 px-4 py-3 text-sm text-success">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
        Access is fail-closed and checked by the database RPC, not only by the UI.
      </div>

      {revokedRows.length > 0 && (
        <details className="rounded-lg border border-navy-border p-4">
          <summary className="cursor-pointer text-sm font-medium text-ink-primary">Revocation history ({revokedRows.length})</summary>
          <div className="mt-3 space-y-2">
            {revokedRows.map((row) => (
              <div key={row.organization_id} className="flex flex-wrap items-center justify-between gap-3 border-t border-navy-border pt-3 text-sm">
                <div><span className="text-ink-primary">{row.organization_name}</span><span className="ml-2 text-ink-muted">revoked {formatDate(row.revoked_at)}</span></div>
                <Button type="button" variant="secondary" size="sm" onClick={() => enrollMutation.mutate({ orgId: row.organization_id, enrollmentNotes: row.notes })} loading={enrollMutation.isPending}><RotateCcw className="h-4 w-4" />Restore</Button>
              </div>
            ))}
          </div>
        </details>
      )}

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={`Add organization to ${title}`}
        footer={<><Button type="button" variant="secondary" onClick={() => setModalOpen(false)}>Cancel</Button><Button type="button" disabled={!organizationId} loading={enrollMutation.isPending} onClick={() => enrollMutation.mutate({ orgId: organizationId, enrollmentNotes: notes.trim() || null })}>Add to beta</Button></>}
      >
        <div className="space-y-4">
          <label className="block text-sm text-ink-secondary">Search real organizations
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Organization name" className="mt-1 w-full rounded-lg border border-navy-border bg-navy px-3 py-2 text-ink-primary" />
          </label>
          <label className="block text-sm text-ink-secondary">Organization
            <select value={organizationId} onChange={(event) => setOrganizationId(event.target.value)} className="mt-1 w-full rounded-lg border border-navy-border bg-navy px-3 py-2 text-ink-primary">
              <option value="">Select an organization</option>
              {candidates.map((org) => <option key={org.id} value={org.id}>{org.name} · {org.slug}</option>)}
            </select>
          </label>
          <label className="block text-sm text-ink-secondary">Internal note
            <textarea value={notes} onChange={(event) => setNotes(event.target.value)} rows={3} placeholder="Why this organization joined the pilot" className="mt-1 w-full rounded-lg border border-navy-border bg-navy px-3 py-2 text-ink-primary" />
          </label>
          <p className="text-xs text-ink-muted">Demo and test organizations are excluded by both the UI and the database.</p>
        </div>
      </Modal>
    </section>
  );
}
